import { useCallback } from "react";
import { useNavigate, useParams } from "react-router";

/**
 * Replaces route parameters in a URL pattern with actual values
 * @param pattern - The URL pattern with parameters (e.g., "/workspaces/:workspaceId/dashboard")
 * @param params - The parameters to inject into the pattern
 * @returns The URL with parameters replaced
 */
export function injectParams(pattern: string, params: Record<string, string>): string {
  return Object.entries(params).reduce((url, [key, value]) => {
    // Use a global regex to replace all occurrences of the parameter
    const paramRegex = new RegExp(`:${key}`, "g");
    return url.replace(paramRegex, value);
  }, pattern);
}

/**
 * Type for navigation state
 */
type NavigationState = Record<string, unknown>;

/**
 * Custom hook that provides navigation functions with the current workspaceId automatically injected
 */
export function useWorkspaceNavigate() {
  const navigate = useNavigate();
  const { workspaceId } = useParams<{ workspaceId: string }>();

  /**
   * Navigate to a route with the current workspaceId automatically injected
   * @param route - The route pattern to navigate to
   * @param options - Navigation options to pass to useNavigate
   */
  const navigateWithWorkspace = useCallback(
    (route: string, options?: { replace?: boolean; state?: NavigationState }) => {
      if (!workspaceId) {
        console.warn("No workspaceId found in params, navigation might not work as expected");
        navigate(route, options);
        return;
      }

      const url = injectParams(route, { workspaceId });
      navigate(url, options);
    },
    [navigate, workspaceId],
  );

  return navigateWithWorkspace;
}
